import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AlertAssignment,
  AlertAssignmentRequest,
  AlertAuditEntry,
  AlertComment,
  AlertFeedback,
  AlertStatus,
  VigimareAlert
} from '../models/vigimare-alert.model';
import {VigimareVesselResponse} from '../models/vigimare-vessel.model';
import {VigimareObjectResponse} from '../models/vigimare-object.model';
import {environment} from '../../environments/environment';
import { NgZone } from '@angular/core';
import {KeycloakService} from '../security/keycloak.service';
import {fetchEventSource} from '@microsoft/fetch-event-source';

/** An alert arriving or changing, plus the marker that says the stream was re-established. */
export type AlertStreamEvent =
  | { type: 'created' | 'updated'; alert: VigimareAlert }
  | { type: 'reconnected' };

type AlertEvent = Extract<AlertStreamEvent, { alert: VigimareAlert }>;

@Injectable({
  providedIn: 'root'
})
export class VigimareAlertService {
  private keycloakService = inject(KeycloakService);
  private http = inject(HttpClient);
  private zone = inject(NgZone);

  private readonly alertsApiUrl = `${environment.apiBaseUrl}/vigimare-alerts`;
  private readonly vesselsApiUrl = `${environment.apiBaseUrl}/vigimare-vessels`;
  private readonly objectsApiUrl = `${environment.apiBaseUrl}/vigimare-objects`;


  getAll(): Observable<VigimareAlert[]> {
    return this.http.get<VigimareAlert[]>(this.alertsApiUrl);
  }

  getAssigned(scope: 'all' | 'me' | 'organizations'): Observable<VigimareAlert[]> {
    return this.http.get<VigimareAlert[]>(`${this.alertsApiUrl}/assigned`, {
      params: { scope }
    });
  }

  getAssignments(alertId: number): Observable<AlertAssignment[]> {
    return this.http.get<AlertAssignment[]>(`${this.alertsApiUrl}/${alertId}/assignments`);
  }

  addAssignments(alertId: number, request: AlertAssignmentRequest): Observable<AlertAssignment[]> {
    return this.http.post<AlertAssignment[]>(`${this.alertsApiUrl}/${alertId}/assignments`, request);
  }

  removeAssignment(alertId: number, assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.alertsApiUrl}/${alertId}/assignments/${assignmentId}`);
  }

  getAudit(alertId: number): Observable<AlertAuditEntry[]> {
    return this.http.get<AlertAuditEntry[]>(`${this.alertsApiUrl}/${alertId}/audit`);
  }

  updateStatus(alertId: number, status: AlertStatus): Observable<VigimareAlert> {
    return this.http.patch<VigimareAlert>(`${this.alertsApiUrl}/${alertId}/status`, { status });
  }

  addComment(alertId: number, commentText: string): Observable<AlertComment> {
    return this.http.post<AlertComment>(`${this.alertsApiUrl}/${alertId}/comments`, { commentText });
  }

  getCurrentUserFeedback(alertId: number): Observable<AlertFeedback | null> {
    return this.http.get<AlertFeedback | null>(`${this.alertsApiUrl}/${alertId}/feedback/me`);
  }

  addFeedback(alertId: number, rating: number, message: string): Observable<AlertFeedback> {
    return this.http.post<AlertFeedback>(`${this.alertsApiUrl}/${alertId}/feedback`, { rating, message });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.alertsApiUrl}/${id}`);
  }

  getVesselById(id: number): Observable<VigimareVesselResponse> {
    return this.http.get<VigimareVesselResponse>(`${this.vesselsApiUrl}/${id}`);
  }

  getObjectById(id: number): Observable<VigimareObjectResponse> {
    return this.http.get<VigimareObjectResponse>(`${this.objectsApiUrl}/${id}`);
  }

  /**
   * Alert events only — what a caller that just wants alerts should subscribe to.
   */
  listenForAlertEvents(): Observable<AlertEvent> {
    return this.listenForAlertStream().pipe(
      filter((event): event is AlertEvent => event.type !== 'reconnected'),
    );
  }

  /**
   * The raw stream, including a `reconnected` marker.
   *
   * A stream can drop — the server's emitter times out every 30 minutes by design, and a
   * reverse proxy can cut it sooner — and the 5s retry below reopens it, but nothing replays
   * what was pushed while it was down. A caller that keeps its own view of the alerts needs to
   * know the connection is new so it can re-read history; otherwise the only cure is a page
   * reload, which is exactly the symptom this is here to prevent.
   */
  listenForAlertStream(): Observable<AlertStreamEvent> {
    return new Observable((observer) => {
      let destroyed = false;
      let currentAbort: AbortController | null = null;
      // The server greets every subscription with `connected`. The first one is the caller's own
      // initial load, which is already fetching history; only the later ones mean a gap.
      let hasConnectedBefore = false;

      const connect = async () => {
        if (destroyed) return;

        currentAbort = new AbortController();

        try {
          const token = await this.keycloakService.updateToken();
          if (destroyed) return;

          await fetchEventSource(this.alertsApiUrl + '/stream', {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            signal: currentAbort.signal,

            // Without this the library aborts the stream whenever the tab is hidden and reopens it
            // on return — and nothing replays what was pushed in between. An alert raised while the
            // operator was looking at another tab would simply never arrive.
            openWhenHidden: true,

            onmessage: (event) => {
              if (event.event === 'connected') {
                if (hasConnectedBefore) {
                  this.zone.run(() => observer.next({ type: 'reconnected' }));
                }
                hasConnectedBefore = true;
              }
              if (event.event === 'new-alert') {
                this.zone.run(() => observer.next({ type: 'created', alert: JSON.parse(event.data) }));
              }
              if (event.event === 'updated-alert') {
                this.zone.run(() => observer.next({ type: 'updated', alert: JSON.parse(event.data) }));
              }
            },

            onerror: (error) => {
              throw error;
            },
          });

          // Reached when the server ended the stream cleanly — the emitter's own timeout does this
          // every 30 minutes. The library only retries from its error path, so without this the
          // page goes quiet until someone reloads it.
          if (destroyed) return;
          setTimeout(connect, 5000);
        } catch {
          if (destroyed) return;
          setTimeout(connect, 5000);
        }
      };

      connect();

      return () => {
        destroyed = true;
        currentAbort?.abort();
      };
    });
  }
}
